import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellRing,
  CheckCheck,
  ExternalLink,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { api, ApiError, query } from "../api";
import {
  pushPermissionHelp,
  pushSetupHelp,
  syncPushSubscription,
  type PushSyncState,
} from "../push";
import type { ApiList, AppSettings, LeaveRequest, NotificationItem, User } from "../types";
import { addIsoDays, dateLabel, dateRangeLabel, LEAVE_LABELS, localDateTime, roleLabel, todayIso } from "../utils";
import { Alert, Button, Empty, Loading, Modal, PageHeader, StatusBadge } from "../components/ui";
import { notificationRoute } from "../notification-route";

interface NotificationLeaveDetail extends LeaveRequest {
  loginCode?: string;
  managementShift?: string | null;
  approverRole?: string | null;
  approvedAt?: string | null;
}

function list(
  value:
    | ApiList<NotificationItem>
    | NotificationItem[]
    | { notifications: NotificationItem[] },
): NotificationItem[] {
  return Array.isArray(value)
    ? value
    : "items" in value
      ? value.items
      : value.notifications;
}
type NotificationResponse = ApiList<NotificationItem> & { unreadCount?: number };

function notificationDay(value: string) {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
}

function notificationMessage(value: string) {
  return value
    .replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})/g, (match) => localDateTime(match))
    .replace(/\b\d{4}-\d{2}-\d{2}\b/g, (match) => dateLabel(match, "short"));
}

export function NotificationsScreen({
  settings,
  currentUser,
  navigate,
  onUnreadChange,
  notify,
}: {
  settings: AppSettings;
  currentUser: User;
  navigate: (path: string) => void;
  onUnreadChange: (count: number) => void;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const loadVersion = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"all" | "unread" | "read">("all");
  const [pushLoading, setPushLoading] = useState(false);
  const [pushState, setPushState] = useState<PushSyncState | null>(null);
  const [pushError, setPushError] = useState('');
  const [leaveDetail, setLeaveDetail] = useState<NotificationLeaveDetail | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  function toggleSelection(id: string) {
    if (deleting) return;
    setSelectedIds(ids => ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id]);
  }
  async function removeSelected() {
    if (deleting || !selectedIds.length) return;
    setDeleting(true);
    try {
      const result = await api<{deleted:number;unreadCount:number}>("/api/notifications/bulk-delete", {method:"POST",body:{ids:selectedIds}});
      loadVersion.current++;setLoading(false);
      setItems(current => current.filter(item => !selectedIds.includes(String(item.id))));
      onUnreadChange(result.unreadCount);
      setSelectedIds([]);setSelecting(false);
      notify(`${result.deleted} bildirim silindi.`);
      await load();
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Seçili bildirimler silinemedi. Tekrar deneyin.", "danger");
    } finally { setDeleting(false); }
  }
  const groupedItems = useMemo(() => {
    const today = todayIso();
    const yesterday = addIsoDays(today, -1);
    const groups = [
      { key: "today", label: "Bugün", items: [] as NotificationItem[] },
      { key: "yesterday", label: "Dün", items: [] as NotificationItem[] },
      { key: "older", label: "Daha eski", items: [] as NotificationItem[] },
    ];
    for (const item of items) {
      const day = notificationDay(item.createdAt);
      const target = day === today ? groups[0] : day === yesterday ? groups[1] : groups[2];
      target?.items.push(item);
    }
    return groups.filter((group) => group.items.length);
  }, [items]);
  async function load() {
    const version = ++loadVersion.current;
    setLoading(true);
    setError("");
    try {
      const payload = await api<
        | NotificationResponse
        | NotificationItem[]
        | { notifications: NotificationItem[]; unreadCount?: number }
      >(`/api/notifications${query({ status })}`);
      if (version !== loadVersion.current) return;
      const result = list(payload);
      setItems(result);
      setSelectedIds(ids => ids.filter(id => result.some(item => String(item.id) === id)));
      onUnreadChange(
        Array.isArray(payload)
          ? result.filter((item) => !item.read).length
          : (payload.unreadCount ?? result.filter((item) => !item.read).length),
      );
    } catch (caught) {
      if (version !== loadVersion.current) return;
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Bildirimler yüklenemedi.",
      );
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [status]);
  async function refreshPush(requestPermission = false) {
    const state = await syncPushSubscription(settings.vapidPublicKey, {
      requestPermission,
    });
    setPushState(state);
    setPushError('');
    return state;
  }
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") void refreshPush().catch(e => setPushError(e instanceof Error ? e.message : 'Bildirim durumu kontrol edilemedi.'));
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [settings.vapidPublicKey]);
  async function read(item: NotificationItem) {
    if (!item.read) {
      try {
        await api(`/api/notifications/${item.id}/read`, { method: "POST" });
        setItems((current) =>
          current.map((entry) =>
            entry.id === item.id ? { ...entry, read: true } : entry,
          ),
        );
        onUnreadChange(
          Math.max(0, items.filter((entry) => !entry.read).length - 1),
        );
      } catch (caught) {
        notify(
          caught instanceof ApiError
            ? caught.message
            : "Bildirim güncellenemedi.",
          "danger",
        );
        return;
      }
    }
    if (item.entityType === "leave_request" && item.entityId && item.link
      && !/\/(?:approvals|requests)(?:\?|$)/.test(item.link)) {
      try {
        setLeaveDetail(await api<NotificationLeaveDetail>(`/api/leaves/${item.entityId}/detail`));
        return;
      } catch {
        // Kayıt erişilebilir değilse bildirimin güvenli bağlantısına devam edilir.
      }
    }
    const target = notificationRoute(item, currentUser);
    if (target) navigate(target);
  }
  async function readAll() {
    try {
      await api("/api/notifications/read-all", { method: "POST" });
      loadVersion.current++; setLoading(false);
      setItems((current) => status === "unread" ? [] : current.map((item) => ({ ...item, read: true })));
      onUnreadChange(0);
      notify("Tüm bildirimler okundu olarak işaretlendi.");
    } catch (caught) {
      notify(
        caught instanceof ApiError
          ? caught.message
          : "Bildirimler güncellenemedi.",
        "danger",
      );
    }
  }
  async function remove(item: NotificationItem) {
    if (!window.confirm("Bu bildirim silinsin mi?")) return;
    try {
      await api(`/api/notifications/${item.id}`, { method: "DELETE" });
      loadVersion.current++; setLoading(false);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      if (!item.read) onUnreadChange(Math.max(0, items.filter((entry) => !entry.read).length - 1));
      notify("Bildirim silindi.");
      await load();
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Bildirim silinemedi.", "danger");
    }
  }
  async function removeRead() {
    if (!window.confirm("Okunmuş bildirimlerin tamamı silinsin mi?")) return;
    try {
      await api("/api/notifications/read", { method: "DELETE" });
      loadVersion.current++; setLoading(false);
      setItems((current) => current.filter((item) => !item.read));
      notify("Okunmuş bildirimler silindi.");
      await load();
    } catch (caught) {
      notify(caught instanceof ApiError ? caught.message : "Okunmuş bildirimler silinemedi.", "danger");
    }
  }
  async function enablePush() {
    if (!settings.vapidPublicKey)
      return notify(
        "Tarayıcı bildirimi sunucuda henüz yapılandırılmamış.",
        "info",
      );
    setPushLoading(true);
    try {
      const state = await refreshPush(true);
      if (!state.supported)
        return notify(pushSetupHelp(), "info");
      if (state.permission === "denied")
        return notify(pushPermissionHelp(), "info");
      if (!state.subscribed)
        return notify("Bildirim aboneliği oluşturulamadı. PWA kurulumunu ve cihaz ayarlarını kontrol edin.", "danger");
      notify("Tarayıcı bildirimleri açıldı.");
    } catch (caught) {
      notify(
        caught instanceof Error
          ? caught.message
          : "Tarayıcı bildirimi açılamadı.",
        "danger",
      );
    } finally {
      setPushLoading(false);
    }
  }
  async function testPush() {
    setPushLoading(true);
    try {
      const state = await refreshPush();
      if (!state.subscribed) {
        notify("Aktif bildirim aboneliği bulunmuyor. Önce bildirimleri açın.", "danger");
        return;
      }
      await api("/api/push/test", { method: "POST" });
      notify("Test bildirimi gönderildi");
    } catch (caught) {
      notify(
        caught instanceof ApiError ? caught.message : "Test bildirimi gönderilemedi.",
        "danger",
      );
    } finally {
      setPushLoading(false);
    }
  }
  return (
    <div className="page">
      <PageHeader
        eyebrow="BİLDİRİM MERKEZİ"
        title="Bildirimler"
        description="Talep, onay, vekâlet, bakiye ve iş devri gelişmeleri."
        actions={
          <div className="button-row">
            <Button
              variant="secondary"
              onClick={() => void enablePush()}
              loading={pushLoading}
            >
              <BellRing size={18} />
              {pushState?.subscribed ? "Bildirimler açık" : "Bildirimleri aç"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => void testPush()}
              disabled={pushLoading || !pushState?.subscribed}
            >
              <Bell size={18} />
              Test bildirimi gönder
            </Button>
            <Button
              variant="ghost"
              onClick={() => void readAll()}
              disabled={selecting || !items.some((item) => !item.read)}
            >
              <CheckCheck size={18} />
              Tümünü okundu yap
            </Button>
          </div>
        }
      />
      {pushState?.permission === "denied" ? <Alert>{pushPermissionHelp()}</Alert> : null}
      {pushError ? <Alert tone="danger">{pushError}</Alert> : null}
      {pushState && !pushState.supported ? <Alert>{pushSetupHelp()}</Alert> : null}
      <div className="notification-filter">
        <div className="segmented notification-status-tabs" role="group" aria-label="Bildirim durumu">
          {([['all', 'Tümü'], ['unread', 'Okunmamış'], ['read', 'Okunmuş']] as const).map(([value, label]) => (
            <button type="button" className={status === value ? "active" : ""} aria-pressed={status === value} disabled={deleting} onClick={() => {setStatus(value);setSelectedIds([]);}} key={value}>{label}</button>
          ))}
        </div>
        <div className="button-row">
          <Button variant="secondary" aria-pressed={selecting} disabled={deleting || !items.length} onClick={() => {setSelecting(value => !value);setSelectedIds([]);}}>
            <Trash2 size={17} /> {selecting ? "Seçimi iptal et" : "Toplu bildirim sil"}
          </Button>
          <Button variant="ghost" onClick={() => void removeRead()} disabled={selecting || !items.some((item) => item.read)}>
            <Trash2 size={17} /> Okunmuşları sil
          </Button>
          <Button variant="ghost" disabled={deleting} onClick={() => void load()}>
            <RefreshCw size={17} /> Yenile
          </Button>
        </div>
      </div>
      {selecting && <p className="muted">Silmek istediğiniz bildirimleri seçin. Seçim sırasında bildirimler açılmaz veya okundu olarak işaretlenmez.</p>}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading && items.length ? <div className="notification-refresh-status" role="status" aria-live="polite">Bildirimler güncelleniyor…</div> : null}
      {loading && !items.length ? (
        <Loading label="Bildirimler yükleniyor" />
      ) : items.length ? (
        <div className="notification-groups" aria-busy={loading}>
          {groupedItems.map((group) => <section className="notification-group" aria-labelledby={`notification-group-${group.key}`} key={group.key}>
            <h2 id={`notification-group-${group.key}`}>{group.label}</h2>
            <div className="notification-list">
              {group.items.map((item) => (
                <article className={`notification-item ${selecting ? "notification-item--selecting" : ""} ${item.read ? "notification-item--read" : ""} ${selecting && selectedIds.includes(String(item.id)) ? "notification-item--selected" : ""}`} key={item.id}>
                  {selecting && <input className="notification-select" type="checkbox" aria-label={`${item.title} bildirimini seç`} checked={selectedIds.includes(String(item.id))} disabled={deleting} onChange={() => toggleSelection(String(item.id))} />}
                  <button type="button" className="notification-item__main" disabled={deleting} onClick={() => selecting ? toggleSelection(String(item.id)) : void read(item)}>
                    <span className="notification-item__icon">{item.read ? <Bell /> : <BellRing />}</span>
                    <span className="notification-item__content">
                      <strong>{item.title}</strong><span>{notificationMessage(item.message)}</span><time>{localDateTime(item.createdAt)}</time>
                    </span>
                    {item.link && !selecting ? <ExternalLink size={17} aria-label="İlgili kayda git" /> : null}
                    {!item.read ? <i aria-label="Okunmamış" /> : null}
                  </button>
                  {!selecting && <button type="button" className="notification-item__delete" aria-label={`${item.title} bildirimini sil`} onClick={() => void remove(item)}><Trash2 size={17} /></button>}
                </article>
              ))}
            </div>
          </section>)}
        </div>
      ) : (
        <Empty
          icon={<Bell />}
          title="Bildirim yok"
          text={
            status === "unread"
              ? "Okunmamış bildiriminiz bulunmuyor."
              : status === "read"
                ? "Okunmuş bildiriminiz bulunmuyor."
              : "Henüz bir bildiriminiz bulunmuyor."
          }
        />
      )}
      {selecting && <div className="notification-bulk-bar" role="region" aria-label="Toplu bildirim silme">
        <strong aria-live="polite">{selectedIds.length} bildirim seçildi</strong>
        <div className="button-row">
          <Button variant="secondary" disabled={deleting || loading || !items.length} onClick={() => setSelectedIds(selectedIds.length === items.length ? [] : items.map(item => String(item.id)))}>{selectedIds.length === items.length ? "Seçimi kaldır" : "Listelenenleri seç"}</Button>
          <Button variant="ghost" disabled={deleting} onClick={() => {setSelecting(false);setSelectedIds([]);}}>Vazgeç</Button>
          <Button loading={deleting} disabled={!selectedIds.length || loading} onClick={() => void removeSelected()}><Trash2 size={17} /> Seçilenleri sil ({selectedIds.length})</Button>
        </div>
      </div>}
      <Modal open={Boolean(leaveDetail)} onClose={() => setLeaveDetail(null)} title="İzin / yokluk ayrıntısı">
        {leaveDetail ? <div className="form-stack">
          <div><strong>{leaveDetail.displayName}</strong><br /><small>{leaveDetail.loginCode ?? ""} · {leaveDetail.unit ?? ""} · {leaveDetail.team ?? leaveDetail.managementShift ?? "R1/R2"}</small></div>
          <div><b>{LEAVE_LABELS[leaveDetail.type]}</b><br /><span>{dateRangeLabel(leaveDetail.startDate, leaveDetail.endDate)}</span></div>
          <StatusBadge status={leaveDetail.status} />
          {leaveDetail.description ? <Alert>Özel açıklama: {leaveDetail.description}</Alert> : <small>Özel açıklama girilmemiş.</small>}
          {leaveDetail.approverName ? <small>{leaveDetail.approverName}{leaveDetail.approverRole ? ` · ${roleLabel(leaveDetail.approverRole)}` : ""}{leaveDetail.approvedAt ? ` · ${localDateTime(leaveDetail.approvedAt)}` : ""}</small> : null}
        </div> : null}
      </Modal>
    </div>
  );
}
